/**
 * Black Tulip v3 — Find Your Size.
 * One measurement, answered for every measured style at once, from the same
 * engine the product page drawer uses.
 */
(function () {
  'use strict';

  function init(root) {
    var payload = root.querySelector('[data-v3-fys-styles]');
    var slider  = root.querySelector('[data-v3-fys-foot]');
    var readout = root.querySelector('[data-v3-fys-readout]');
    var results = root.querySelector('[data-v3-fys-results]');
    var note    = root.querySelector('[data-v3-fys-note]');

    /*
     * THE COPY ARRIVES FROM THE LOCALE, and there is no fallback — 17 September.
     *
     * These nine strings used to sit in a `STR` object at the foot of this
     * file. They are customer-visible sentences and they had never been through
     * the copy route, because nothing in the build made them: an asset cannot
     * call `t`, so they were written here and forgotten. Sumit asked for them
     * moved and sections/v3-find-your-size.liquid now hands them over the same
     * way snippets/v3-size-finder.liquid hands the drawer its own.
     *
     * Keeping the old table as a fallback would have been the obvious thing and
     * it is the wrong one: it is the same nine sentences again, in the same
     * file, free to drift from the locale the day anybody edits one. So if the
     * attribute does not arrive, this returns and the grid is simply absent.
     * The instructions, the tables and the FAQ are server-rendered and are
     * untouched by it. An absent grid gets noticed; unapproved English does not.
     */
    var L = null;
    try { L = JSON.parse(root.getAttribute('data-strings') || 'null'); }
    catch (e) { L = null; }

    if (!payload || !slider || !results || !window.BTFit || !L) return;

    function T(k) { return L[k] || ''; }

    var styles;
    try { styles = JSON.parse(payload.textContent); } catch (e) { return; }
    if (!styles || !styles.length) return;

    // NOTHING BELOW THE SLIDER MOVES — Sumit, 1 October 2026, on the drawer's
    // answer box ("the box should not jump"), and the same was true here: as a
    // drag crossed a tight fit or an outlier, a card gained its advice or warn
    // line, the row grew, and the cards, the note and everything under them
    // moved — up to 41px at 1440 and 109px on a phone, measured on the live
    // page. Every card now carries both lines in every state: the real line
    // when it applies, otherwise the same line hidden with figures of the same
    // width, so it still takes its space. The note under the grid does the
    // same with its longest wording. See also assets/v3-size-finder.js.
    function slotLine(cls, text, shown) {
      return '<div class="' + cls + '"' + (shown ? '' : ' data-empty aria-hidden="true"') + '>' +
        esc(text) + '</div>';
    }
    var adviceSpare = fmt(T('res_tight'), { next: '8' });
    var warnSpare = fmt(T('res_sizes_off'), { n: '2' });
    var noteLongest = [T('note_all_agree'), fmt(T('note_some_beyond'), { n: 5 }),
      fmt(T('note_some_off'), { n: 5 })].sort(function (x, y) { return y.length - x.length; })[0];

    function render() {
      var foot = parseInt(slider.value, 10);
      if (readout) readout.textContent = String(foot);

      var answers = styles.map(function (s) { return window.BTFit.answer(s, foot); });
      var offs = window.BTFit.outliers(answers);
      var beyond = 0, flagged = 0;

      results.innerHTML = styles.map(function (s, i) {
        var a = answers[i];
        if (!a) return '';
        if (a.beyondRange) {
          beyond++;
          return '<div class="v3-res v3-res--none">' +
            '<div class="v3-res__name">' + esc(s.name) + '</div>' +
            '<div class="v3-res__cat">' + esc(s.designation || '') + '</div>' +
            '<div class="v3-res__beyond">' + esc(T('beyond')) + '</div>' +
            '<div class="v3-res__meta">' +
              esc(fmt(T('fits_up_to'), { style: s.name, n: a.maxFootMm })) + '</div>' +
            slotLine('v3-res__advice', adviceSpare, false) +
            slotLine('v3-res__warn', warnSpare, false) +
            '</div>';
        }
        // Two or more steps from the median across the range.
        var isOut = offs[i] >= 2;
        if (isOut) flagged++;
        return '<div class="v3-res' + (isOut ? ' v3-res--flag' : '') + '">' +
          '<div class="v3-res__name">' + esc(s.name) + '</div>' +
          '<div class="v3-res__cat">' + esc(s.designation || '') + '</div>' +
          '<div class="v3-res__size">' + esc(a.size.replace('/', ' / ')) + '</div>' +
          // THE SUBTRACTION IS GONE — review point 14, 21 September 2026. This
          // line read "{internal} mm internal − {toe} mm toe", which is the
          // working rather than the answer: "There is no need to show this
          // calculation or internal length to the customer." The room figure
          // below it is still computed from both and is the one she can
          // compare her own measurement against.
          '<div class="v3-res__meta">' +
            esc(fmt(T('res_room'), { room: a.roomMm })) + '</div>' +
          (a.tight && a.adviceSize
            ? slotLine('v3-res__advice', fmt(T('res_tight'), { next: a.adviceSize.split('/')[0] }), true)
            : slotLine('v3-res__advice', adviceSpare, false)) +
          (isOut ? slotLine('v3-res__warn', fmt(T('res_sizes_off'), { n: offs[i] }), true)
                 : slotLine('v3-res__warn', warnSpare, false)) +
          '</div>';
      }).join('');

      if (note) {
        var said;
        if (!flagged && !beyond) said = T('note_all_agree');
        else if (!flagged) said = fmt(T('note_some_beyond'), { n: beyond });
        else said = fmt(T('note_some_off'), { n: flagged });
        note.innerHTML = '<span class="v3-fys__sizer" aria-hidden="true">' + esc(noteLongest) +
          '</span><span>' + esc(said) + '</span>';
      }
    }

    // The internal/usable toggle used to be wired here. Removed with the
    // control on 21 September — the table has one basis now, so there is
    // nothing to switch between and no data-basis attribute to set.

    slider.addEventListener('input', render);
    render();
  }

  function esc(v) {
    return String(v).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c];
    });
  }
  function fmt(s, vars) {
    return String(s).replace(/\{(\w+)\}/g, function (m, k) {
      return vars[k] != null ? vars[k] : m;
    });
  }

  /*
   * ARRIVING FROM A PRODUCT PAGE — W-20261008-5 item 3. Each style's chart is a
   * closed accordion. The product page's size-finder link ends in ?style=<key>
   * (snippets/v3-size-finder.liquid), the key being the style's designation,
   * else its name, handleized: the same value sections/v3-find-your-size.liquid
   * stamps on each style as data-fys-style. A matching accordion is opened and
   * scrolled to. #fys-style-<n> does the same for a link that carries the
   * accordion's own id. No match, no JS, or no parameter: nothing happens, and
   * the page is simply all closed. Independent of init(), so it still works if
   * the results grid is absent.
   */
  function openArrivalStyle() {
    var key = '', hash = '';
    try { key = new URLSearchParams(window.location.search).get('style') || ''; } catch (e) { key = ''; }
    try { hash = decodeURIComponent(window.location.hash || '').replace(/^#/, ''); } catch (e) { hash = ''; }
    if (!key && hash.indexOf('fys-style-') !== 0) return;

    var wraps = document.querySelectorAll('[data-fys-style]');
    var hit = null;
    for (var i = 0; i < wraps.length && !hit; i++) {
      var d = wraps[i].querySelector('details');
      if (!d) continue;
      if ((key && wraps[i].getAttribute('data-fys-style') === key) || (hash && d.id === hash)) hit = d;
    }
    if (!hit) return;
    hit.open = true;

    var reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { reduce = false; }
    // After layout, so the opened body is counted in where the top lands.
    window.requestAnimationFrame(function () {
      hit.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    });
  }

  function boot() {
    var roots = document.querySelectorAll('[data-v3-fys]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
    openArrivalStyle();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
